/**
 * Lenavio Careers Embed — settings card.
 *
 * Walks the customer through embedding the Lenavio job feed on
 * their own marketing site (e.g. acmecorp.com/careers). Shows their
 * org slug, three integration options with copy-paste snippets, and
 * a "Preview" link to the live JSON endpoint.
 *
 * No backend mutation — pure read-only docs surface. Plan-gated by
 * ``recruiting`` so we don't pitch this to orgs that can't post jobs.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Briefcase,
  Check,
  Code,
  Copy,
  ExternalLink,
  Globe,
} from "lucide-react";

import { getCurrentOrg } from "@/api/organizations";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { useToast } from "@/hooks/useToast";

const API_ORIGIN =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ||
  "https://app.lenavio.com";

function CodeBlock({ code, label }: { code: string; label: string }) {
  const { addToast } = useToast();
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      addToast("success", `${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      addToast("error", "Could not copy to clipboard");
    }
  }

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border border-border bg-background px-3 py-2 text-xs">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={onCopy}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-[11px] text-text-muted hover:text-text-primary"
        aria-label={`Copy ${label}`}
      >
        {copied ? (
          <>
            <Check className="h-3 w-3" /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" /> Copy
          </>
        )}
      </button>
    </div>
  );
}

export function CareersEmbedCard() {
  const [slug, setSlug] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentOrg()
      .then((o) => {
        if (cancelled) return;
        setSlug(o.slug);
        setName(o.name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const snippets = useMemo(() => {
    const s = slug || "your-org-slug";
    return {
      script: `<div data-lenavio-careers="${s}"></div>\n<script src="${API_ORIGIN}/embed/careers.js" async></script>`,
      iframe: `<iframe\n  src="${API_ORIGIN}/${s}/careers"\n  style="width:100%;min-height:800px;border:0"\n  loading="lazy"\n  title="Open positions"\n></iframe>`,
      api: `GET ${API_ORIGIN}/api/v1/public/careers/${s}\n\n# returns JSON: list of currently-open jobs\n# Use this to render a custom careers page server-side.`,
    };
  }, [slug]);

  const previewUrl = slug
    ? `${API_ORIGIN}/${slug}/careers`
    : `${API_ORIGIN}/your-org-slug/careers`;
  const apiPreviewUrl = slug
    ? `${API_ORIGIN}/api/v1/public/careers/${slug}`
    : null;

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <Briefcase className="h-5 w-5 text-accent" aria-hidden />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              Careers page on your website
            </h3>
            <p className="text-[11px] uppercase tracking-wider text-text-muted">
              Recruiting
            </p>
          </div>
        </div>
        <Badge variant="default">Embed</Badge>
      </div>

      <p className="mb-4 text-sm text-text-muted">
        Show your Lenavio open positions on your own marketing site (e.g.{" "}
        <code className="rounded bg-surface px-1">{name?.toLowerCase().replace(/\s+/g, "") || "yourcompany"}.com/careers</code>).
        Roles you toggle <strong>Publish to careers</strong> on inside Lenavio
        appear here automatically — no extra deploy on your end.
      </p>

      <div className="mb-4 rounded-md border border-dashed border-border bg-background p-3 text-xs">
        <div className="flex items-center gap-2 text-text-muted">
          <Globe className="h-3.5 w-3.5" /> Your org slug:{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-text-primary">
            {slug || "loading…"}
          </code>
        </div>
      </div>

      <div className="space-y-5">
        <section>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            <Code className="h-3.5 w-3.5" /> Option 1 — JS embed (recommended)
          </div>
          <p className="mb-2 text-xs text-text-muted">
            Drop these two lines anywhere on your careers page. The widget
            fetches your live job feed, styles itself, and lets visitors
            click through to apply. Works with any CMS — WordPress, Webflow,
            Framer, plain HTML.
          </p>
          <CodeBlock code={snippets.script} label="JS embed snippet" />
          <p className="mt-2 text-[11px] text-text-muted">
            Optional attributes on the host <code>div</code>:{" "}
            <code>data-lenavio-theme="dark"</code>,{" "}
            <code>data-lenavio-apply-url="https://yoursite.com/jobs/&#123;&#123;job_id&#125;&#125;"</code>{" "}
            (override the apply destination so visitors stay on your domain).
          </p>
        </section>

        <section>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            <Code className="h-3.5 w-3.5" /> Option 2 — Iframe
          </div>
          <p className="mb-2 text-xs text-text-muted">
            Easiest path for site builders that don't allow custom scripts.
            Embeds the Lenavio-hosted careers page inside an iframe.
          </p>
          <CodeBlock code={snippets.iframe} label="Iframe snippet" />
        </section>

        <section>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            <Code className="h-3.5 w-3.5" /> Option 3 — JSON API
          </div>
          <p className="mb-2 text-xs text-text-muted">
            Build a fully custom careers page on your own backend. The
            endpoint is open (no auth, no API key required) and returns
            everything you'd want to render: title, department, location,
            salary, description.
          </p>
          <CodeBlock code={snippets.api} label="API endpoint" />
        </section>

        <div className="flex flex-wrap gap-3 border-t border-border pt-3 text-sm">
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-accent hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Preview hosted page
          </a>
          {apiPreviewUrl && (
            <a
              href={apiPreviewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-text-muted hover:text-text-primary"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View JSON feed
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}

export default CareersEmbedCard;
